import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreRefs
 * @property {HTMLElement} content - The wrapper around the clipped content
 * @property {HTMLElement} toggle - The toggle button
 * @property {HTMLElement} [labelMore] - "Leer más" label
 * @property {HTMLElement} [labelLess] - "Mostrar menos" label
 */

/**
 * Custom element that clips long content (e.g. product descriptions) and
 * exposes a "Leer más / Mostrar menos" toggle. The collapsed height is
 * controlled with the `data-max-height` attribute (in px, default 280).
 *
 * Markup expected:
 *   <read-more data-max-height="280">
 *     <div class="read-more__content" ref="content">...rich text...</div>
 *     <button type="button" class="read-more__toggle" ref="toggle"
 *             on:click="/toggle">
 *       <span ref="labelMore">Leer más</span>
 *       <span ref="labelLess" hidden>Mostrar menos</span>
 *     </button>
 *   </read-more>
 *
 * @extends {Component<ReadMoreRefs>}
 */
class ReadMore extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {boolean} */
  #needsClip = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#measure();
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.refs.content);

    const images = this.refs.content.querySelectorAll('img');
    images.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => this.#measure(), { once: true });
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
  }

  get #maxHeight() {
    const value = parseInt(this.dataset.maxHeight ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : 280;
  }

  #measure = () => {
    const { content, toggle } = this.refs;
    /*
     * scrollHeight reflects the unclipped height even when the element is
     * currently constrained by max-height, so it's safe to measure here.
     */
    const fullHeight = content.scrollHeight;
    const previousNeedsClip = this.#needsClip;
    this.#needsClip = fullHeight > this.#maxHeight + 8;

    if (this.#needsClip) {
      this.dataset.clipped = 'true';
      toggle.hidden = false;
      // JS takes over the clamp once mounted so animations behave consistently.
      content.style.maxHeight = this.#expanded ? `${fullHeight}px` : `${this.#maxHeight}px`;
    } else {
      this.dataset.clipped = 'false';
      toggle.hidden = true;
      content.style.removeProperty('max-height');
      if (previousNeedsClip && this.#expanded) {
        this.#expanded = false;
        this.dataset.expanded = 'false';
        toggle.setAttribute('aria-expanded', 'false');
      }
    }
  };

  /**
   * Toggle expanded / collapsed state.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();
    if (!this.#needsClip) return;

    const { content, toggle, labelMore, labelLess } = this.refs;
    this.#expanded = !this.#expanded;

    if (this.#expanded) {
      content.style.maxHeight = `${content.scrollHeight}px`;
    } else {
      content.style.maxHeight = `${this.#maxHeight}px`;
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    this.dataset.expanded = this.#expanded ? 'true' : 'false';
    toggle.setAttribute('aria-expanded', this.#expanded ? 'true' : 'false');

    if (labelMore && labelLess) {
      labelMore.hidden = this.#expanded;
      labelLess.hidden = !this.#expanded;
    }
  };
}

if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}
